import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { type Subscription, timer } from 'rxjs';

import { ModalType } from '@core/models/modal.types';
import { BaseActionHandlerService } from '@core/services/action-handler/base-action-handler.service';
import { JexlService } from '@core/services/jexl.service';
import { ModalService } from '@core/services/modal.service';

import { type OrderInfoDto, OrderInfoService } from 'swagger-services/order';

import { GroupsRenderer } from '@shared/components/groups-renderer/groups-renderer';
import { IframeModal } from '@shared/modals/iframe/iframe.modal';
import type { ComponentGroup } from '@shared/models/dynamic-layout.types';
import type { DynamicAction } from '@shared/registry';
import { DataState } from '@shared/utils/data-state';

import type { PaymentFailureConfig, PaymentFailureReason } from './payment-failure.types';

const POLLING_INTERVAL_MS = 5000;

@Component({
  selector: 'app-payment-failure',
  template: `
    <app-groups-renderer
      [groups]="interpolatedGroups()"
      [loadingGroups]="loadingGroups"
      [isLoading]="isLoading()"
      (componentAction)="onAction($event)"
    />
  `,
  imports: [GroupsRenderer],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full' },
})
export class PaymentFailure {
  private readonly destroyRef = inject(DestroyRef);
  private readonly baseActionHandler = inject(BaseActionHandlerService);
  private readonly jexlService = inject(JexlService);
  private readonly modalService = inject(ModalService);
  private readonly orderInfoService = inject(OrderInfoService);
  readonly config = input.required<PaymentFailureConfig>();

  readonly paymentComplete = output<DynamicAction>();

  private readonly orderState = signal<DataState<OrderInfoDto>>(DataState.initial());
  private checkOrderSubscription?: Subscription;
  private payDate?: string;

  private readonly actionHandler = this.baseActionHandler.createScope({
    makePayment: () => this.handleMakePayment(),
    updatePaymentMethod: () => this.handleMakePayment(),
    retry: () => this.handleMakePayment(),
  });

  protected readonly isLoading = computed(() => {
    const orderState = this.orderState();
    return orderState.isLoading() || orderState.isInitial();
  });

  private readonly reason = computed<PaymentFailureReason>(() => {
    const order = this.orderState().getData();
    if (!order) return 'system_error';

    switch (order.status) {
      case 'partially-paid':
        return 'partial_payment';
      case 'declined':
        return 'card_declined';
      case 'payment-method-missing':
        return 'missing_payment_method';
      case 'failed':
      default:
        return 'system_error';
    }
  });

  private readonly jexlContext = computed<Record<string, unknown>>(() => {
    const config = this.config();
    const order = this.orderState().getData();
    const reason = this.reason();
    const stateContent = config.states?.[reason] ?? config.states?.system_error;
    const orderItemName = order?.orderItems?.[0]?.name;

    return {
      order,
      totalAmount: order?.totalAmount ?? '0',
      paidAmount: order?.paidAmount ?? '0',
      balanceDue: order?.toBePaidAmount ?? order?.totalAmount ?? '0',
      reason,
      errorTitle: stateContent?.errorTitle ?? '',
      primaryButtonLabel: stateContent?.primaryButtonLabel ?? '',
      primaryAction: stateContent?.primaryAction ?? 'retry',
      orderChip: orderItemName ? { text: orderItemName, variant: 'subtle' } : undefined,
    };
  });

  protected readonly interpolatedGroups = computed<ComponentGroup[]>(() => {
    const config = this.config();
    if (!config.groups) return [];

    // Handle skipped interpolation structure: { value: [...] } or direct array
    const groupsConfig = config.groups as { value?: ComponentGroup[] } | ComponentGroup[];
    const groups = Array.isArray(groupsConfig) ? groupsConfig : (groupsConfig?.value ?? []);

    return this.jexlService.process(groups, this.jexlContext()) as ComponentGroup[];
  });

  protected readonly loadingGroups: ComponentGroup[] = [
    {
      id: 'loading-wrapper',
      layout: {
        direction: 'column',
        gap: 'lg',
        grow: true,
        justify: 'center',
        className: 'px-(--page-padding-x) py-(--page-top-content-y)',
      },
      items: [
        {
          id: 'sk-icon',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '64px', width: '64px', borderRadius: 'full' } },
        },
        {
          id: 'sk-title',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '32px', width: '80%' } },
        },
        {
          id: 'sk-card',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '120px', borderRadius: 'lg' } },
        },
      ],
    },
    {
      id: 'loading-buttons',
      layout: {
        direction: 'column',
        gap: 'md',
        className: 'px-(--page-padding-x) pb-(--page-padding-bottom) pt-4 mt-auto',
      },
      items: [
        {
          id: 'sk-btn1',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '48px', borderRadius: 'full' } },
        },
        {
          id: 'sk-btn2',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '48px', borderRadius: 'full' } },
        },
      ],
    },
  ];

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.checkOrderSubscription?.unsubscribe();
    });

    afterNextRender(() => {
      void this.loadOrder();
    });
  }

  protected onAction(event: DynamicAction): void {
    void this.actionHandler.execute(event);
  }

  private async loadOrder(): Promise<void> {
    const orderId = this.config().orderId;
    if (!orderId) return;

    this.orderState.set(DataState.loading());

    try {
      const order = await this.orderInfoService.orderInfoControllerGetOrderById('', orderId);
      this.payDate = order.payDate;
      this.orderState.set(DataState.success(order));

      if (order.status === 'completed') {
        this.paymentComplete.emit({ id: 'paymentComplete', data: { orderId } });
      }
    } catch (error) {
      this.orderState.set(DataState.error(error instanceof Error ? error : new Error('Failed to load order')));
    }
  }

  private handleMakePayment(): void {
    const fetchUrl = async (): Promise<string> => {
      const payOrder = await this.orderInfoService.orderInfoControllerGetPaymentDeepLink('', this.config().orderId);
      const url = payOrder.data?.webTokenUrl;
      if (!url) throw new Error('Payment URL not available');
      return url;
    };

    const modalTitle = this.config().modalTitle ?? '';
    const modalClosed = this.modalService.openModal(IframeModal, { title: modalTitle, fetchUrl }, ModalType.CARD);

    this.startPolling(modalClosed);
  }

  private startPolling(modalClosed: Promise<unknown>): void {
    const orderId = this.config().orderId;

    this.checkOrderSubscription?.unsubscribe();
    this.checkOrderSubscription = timer(0, POLLING_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.checkOrder(orderId));

    void modalClosed.then(() => {
      this.checkOrderSubscription?.unsubscribe();
      this.checkOrderSubscription = undefined;
      void this.checkOrder(orderId);
    });
  }

  private async checkOrder(orderId: string): Promise<void> {
    try {
      const order = await this.orderInfoService.orderInfoControllerGetOrderById('', orderId);

      switch (order.status) {
        case 'completed': {
          this.modalService.dismissAll();
          this.checkOrderSubscription?.unsubscribe();
          this.checkOrderSubscription = undefined;
          this.paymentComplete.emit({ id: 'paymentComplete', data: { orderId } });
          break;
        }
        case 'failed':
        case 'declined':
        case 'partially-paid':
        case 'payment-method-missing': {
          if (order.payDate !== this.payDate) {
            this.orderState.set(DataState.success(order));
            this.payDate = order.payDate;
            this.modalService.dismissAll();
          }
          break;
        }
      }
    } catch {
      // Continue polling on error
    }
  }
}
